import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import Checkbox from "@mui/material/Checkbox";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDInputSo from "components/MDInputSo";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { danhMucApi } from "api/danhMuc";
import { chuVuonApi } from "api/chuVuon";
import { vuonApi } from "api/vuon";
import { sotApi } from "api/sot";
import { giaoDichApi } from "api/giaoDich";
import { giaTheoNgayApi } from "api/giaTheoNgay";
import { banNhapThuMuaApi } from "api/banNhapThuMua";
import { hopTacXaApi } from "api/hopTacXa";
import {
  coHopTacXaDaNho,
  layHopTacXaDaNho,
  luuHopTacXaDaNho,
  xoaHopTacXaDaNho,
} from "lib/nhoHopTacXa";
import { useAuth } from "context/AuthContext";
import { MAU, SEMANTIC, FONT_HIEU, FONT_CHU } from "assets/theme/avocadoBrand";
import QuetSotDialog from "components/QuetSotDialog";
import { chuXuongDong } from "lib/kieuBangDuLieu";
import useTour from "hooks/useTour";
import buocFormThuMua from "tours/formThuMua";

function ngayHomNay() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// Sinh id cục bộ duy nhất cho 1 khối loại bơ — chỉ dùng làm React key/tham
// chiếu state, không liên quan gì tới ID Firestore.
function sinhIdKhoi() {
  return `khoi-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function taoKhoiMoi() {
  return {
    id: sinhIdKhoi(),
    maLoaiBo: "",
    xong: false,
    // Giá đã đặt hôm nay cho loại bơ của khối này (Loại bơ & Giá) — set 1
    // lần lúc chọn loại bơ (chonLoaiBo), dùng làm TRẦN cho đơn giá mọi dòng
    // trong khối: đơn giá PO chỉ được thấp hơn hoặc bằng, không được cao hơn
    // giá đã đặt. null = loại bơ đó chưa có giá đặt sẵn cho hôm nay, không
    // giới hạn (nhân viên tự chịu trách nhiệm nhập giá).
    giaThamChieu: null,
    dong: [{ maSot: null, khoiLuong: "", donGia: "" }],
  };
}

// 1 khối coi là hoàn chỉnh khi đã chọn loại bơ VÀ mọi dòng (mỗi dòng ứng với
// 1 sọt đã quét) đều đủ sọt + khối lượng + đơn giá, VÀ không dòng nào có đơn
// giá vượt giá đã đặt (giaThamChieu, nếu có) — dùng chung để bật nút "Xong"
// và để suy ra trạng thái xong/mở khi nạp lại dữ liệu có sẵn.
function khoiDaHoanChinh(khoi) {
  return (
    !!khoi.maLoaiBo &&
    khoi.dong.every(
      (d) =>
        d.maSot &&
        d.khoiLuong &&
        d.donGia &&
        (khoi.giaThamChieu == null || Number(d.donGia) <= khoi.giaThamChieu)
    )
  );
}

// Nhóm items[] phẳng (từ 1 PO đã lưu, hoặc 1 bản nháp) thành nhomLoaiBo[] để
// hiển thị theo khối — duyệt tuần tự, item nào maLoaiBo giống hệt item liền
// trước thì gộp cùng khối, khác thì tách khối mới.
// - macDinhXong=true (nạp PO đã lưu, dangSuaPO): dữ liệu chắc chắn hoàn
//   chỉnh (đã qua validate lúc lưu) — mọi khối luôn coi là "xong", thu gọn
//   sẵn, bấm "Sửa" khối nào thì mở khối đó.
// - macDinhXong=false (nạp bản nháp): dữ liệu có thể còn dở dang — tự tính
//   xong theo độ hoàn chỉnh THẬT của từng khối (khoiDaHoanChinh), khối chưa
//   đủ dữ liệu vẫn mở sẵn để nhân viên nhập tiếp ngay, không cần bấm "Sửa".
function nhomHoaItems(items, macDinhXong) {
  const ketQua = [];
  items.forEach((it) => {
    const cuoi = ketQua[ketQua.length - 1];
    const dongMoi = {
      maSot: it.maSot ?? null,
      khoiLuong: it.khoiLuong != null && it.khoiLuong !== "" ? String(it.khoiLuong) : "",
      donGia: it.donGia != null && it.donGia !== "" ? String(it.donGia) : "",
    };
    if (cuoi && cuoi.maLoaiBo === (it.maLoaiBo ?? "")) {
      cuoi.dong.push(dongMoi);
    } else {
      // giaThamChieu: null — khối nạp lại từ PO/nháp đã lưu không áp trần
      // giá (dữ liệu cũ, không biết giá đã đặt lúc đó là bao nhiêu); trần
      // giá chỉ tính lại nếu nhân viên chọn lại loại bơ (xem chonLoaiBo).
      ketQua.push({
        id: sinhIdKhoi(),
        maLoaiBo: it.maLoaiBo ?? "",
        xong: false,
        giaThamChieu: null,
        dong: [dongMoi],
      });
    }
  });
  ketQua.forEach((khoi) => {
    khoi.xong = macDinhXong || khoiDaHoanChinh(khoi);
  });
  return ketQua;
}

// Chiều cao cố định cho các ô nhập 1 dòng (select lẫn text/number) — ép bằng
// "height" thay vì canh padding, vì theme dùng chung
// (assets/theme/components/form/select.js) ép padding ".MuiSelect-select"
// bằng !important nên riêng ô select luôn thấp hơn nếu chỉ canh padding.
const CHIEU_CAO_O = "32px";

const oVienInput = {
  borderRadius: "10px",
  backgroundColor: SEMANTIC.surface,
  fontFamily: FONT_CHU,
  "& fieldset": { borderColor: alpha(MAU.skin, 0.22) },
  "&:hover fieldset": { borderColor: alpha(MAU.skin, 0.5) },
  "&.Mui-focused fieldset": { borderColor: MAU.pit, borderWidth: "2px" },
};

const oInput = {
  "& .MuiOutlinedInput-root": { ...oVienInput, height: CHIEU_CAO_O },
  "& .MuiOutlinedInput-input": {
    height: "100%",
    boxSizing: "border-box",
    padding: "0 14px",
  },
  "& .MuiSelect-select": {
    height: "100% !important",
    boxSizing: "border-box !important",
    padding: "0 14px !important",
    display: "flex !important",
    alignItems: "center !important",
  },
};

// Ghi chú là textarea nhiều dòng — không ép cùng chiều cao với ô 1 dòng,
// chỉ dùng chung màu viền/bo góc.
const oGhiChu = {
  "& .MuiOutlinedInput-root": oVienInput,
  "& .MuiOutlinedInput-input": { padding: "13px 14px" },
};

function Nhan({ children, sx }) {
  return (
    <MDTypography
      sx={{
        display: "block",
        mb: 0.75,
        fontFamily: FONT_CHU,
        fontWeight: 600,
        fontSize: "0.68rem",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: alpha(MAU.ink, 0.55),
        ...sx,
      }}
    >
      {children}
    </MDTypography>
  );
}

Nhan.propTypes = {
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

Nhan.defaultProps = {
  sx: {},
};

function Don({ nhan, giaTri }) {
  return (
    <Grid item xs={12}>
      <MDBox sx={chuXuongDong}>
        <MDTypography
          component="span"
          sx={{
            fontFamily: FONT_CHU,
            fontWeight: 600,
            fontSize: "0.68rem",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: alpha(MAU.ink, 0.55),
          }}
        >
          {nhan}:{" "}
        </MDTypography>
        <MDTypography
          component="span"
          sx={{
            fontFamily: FONT_HIEU,
            fontWeight: 600,
            fontSize: "1.05rem",
            color: MAU.ink,
          }}
        >
          {giaTri}
        </MDTypography>
      </MDBox>
    </Grid>
  );
}

Don.propTypes = {
  nhan: PropTypes.string.isRequired,
  giaTri: PropTypes.string.isRequired,
};

function DonVi({ children }) {
  return (
    <MDTypography sx={{ fontFamily: FONT_CHU, fontSize: "0.8rem", color: alpha(MAU.ink, 0.5) }}>
      {children}
    </MDTypography>
  );
}

DonVi.propTypes = {
  children: PropTypes.node.isRequired,
};

// Badge "Sọt: <mã>" clicável — dùng cả cho dòng đang mở (đổi sọt của dòng
// đó) lẫn để liệt kê sọt trong khối đã "Xong" (không click được, chỉ hiển
// thị) qua prop `clicavel`. `phu` (vd "500/500 kg") thêm phần "đã cân/sức
// chứa" ngay trên badge — chỉ dùng ở khối đã Xong, để biết ngay sọt nào gần
// đầy mà không cần mở lại khối.
function BadgeSot({ maHienThi, phu, onClick }) {
  const Thanh = onClick ? "button" : "div";
  return (
    <MDBox
      component={Thanh}
      type={onClick ? "button" : undefined}
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        px: 0.9,
        py: 0.25,
        borderRadius: "999px",
        border: `1px solid ${alpha(MAU.pit, 0.35)}`,
        backgroundColor: alpha(MAU.pit, 0.1),
        cursor: onClick ? "pointer" : "default",
        ...(onClick ? { "&:hover": { backgroundColor: alpha(MAU.pit, 0.18) } } : {}),
      }}
    >
      <Icon fontSize="small" sx={{ fontSize: "0.75rem !important", color: MAU.pit }}>
        inventory_2
      </Icon>
      <MDTypography
        sx={{
          fontFamily: FONT_CHU,
          fontWeight: 700,
          fontSize: "0.62rem",
          letterSpacing: "0.03em",
          color: MAU.pit,
        }}
      >
        Sọt: {maHienThi ?? "—"}
        {phu ? ` · ${phu}` : ""}
      </MDTypography>
      {onClick && (
        <Icon fontSize="small" sx={{ fontSize: "0.85rem !important", color: MAU.pit }}>
          arrow_drop_down
        </Icon>
      )}
    </MDBox>
  );
}

BadgeSot.propTypes = {
  maHienThi: PropTypes.string,
  phu: PropTypes.string,
  onClick: PropTypes.func,
};

BadgeSot.defaultProps = {
  maHienThi: null,
  phu: null,
  onClick: null,
};

// Nút dạng ô viền chấm chấm dùng cho mọi hành động "quét/thêm" trong khối
// (Quét QR Sọt lần đầu, Thêm sọt khi đầy) — cùng 1 kiểu để nhân viên
// nhận ra ngay đây là hành động cần camera.
function NutVienChamChamMau({ id, mau, icon, children, onClick }) {
  return (
    <MDBox
      id={id}
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        py: 1.25,
        borderRadius: "10px",
        border: `1.5px dashed ${alpha(mau, 0.5)}`,
        backgroundColor: "transparent",
        cursor: "pointer",
        fontFamily: FONT_CHU,
        fontWeight: 600,
        fontSize: "0.85rem",
        color: mau,
        transition: "background-color 120ms ease, border-color 120ms ease",
        "&:hover": { backgroundColor: alpha(mau, 0.08), borderColor: alpha(mau, 0.8) },
        "&:focus-visible": { outline: `3px solid ${alpha(MAU.pit, 0.5)}`, outlineOffset: "2px" },
      }}
    >
      <Icon fontSize="small">{icon}</Icon>
      {children}
    </MDBox>
  );
}

NutVienChamChamMau.propTypes = {
  id: PropTypes.string,
  mau: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
};

NutVienChamChamMau.defaultProps = {
  id: undefined,
};

function FormThuMua() {
  const { maChuVuon, maVuon } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { user } = useAuth();
  // Có "suaPOId" (mang từ nút "Quay lại sửa PO" ở XacNhanThanhToan/Giao dịch
  // của tôi) = đang sửa lại 1 PO đã lưu, không phải nhập mới — PO tự fetch
  // lại items[] hiện tại qua giaoDichApi.chiTiet bên dưới.
  const suaPOId = state?.suaPOId ?? null;
  const dangSuaPO = !!suaPOId;

  const [ngoaiCanh, setNgoaiCanh] = useState(null);
  const [loaiBoList, setLoaiBoList] = useState([]);
  const [hinhThucTtList, setHinhThucTtList] = useState([]);
  const [hopTacXaList, setHopTacXaList] = useState([]);
  const [dangTai, setDangTai] = useState(true);
  const [dangLuu, setDangLuu] = useState(false);
  const [loi, setLoi] = useState("");

  // Không còn 1 sọt "cố định" cho cả phiếu — mỗi khối loại bơ tự quét sọt
  // riêng ngay khi bắt đầu (xem form.nhomLoaiBo bên dưới).
  const [form, setForm] = useState({
    nhomLoaiBo: [taoKhoiMoi()],
    hinhThucTt: "tien_mat",
    ghiChu: "",
    maHopTacXa: "",
  });
  // Nhớ hợp tác xã đã chọn cho lần "Nhập thu mua" tiếp theo (localStorage,
  // riêng máy này) — mặc định bật sẵn nếu đã có sẵn 1 mã được nhớ từ trước.
  const [nhoHopTacXa, setNhoHopTacXa] = useState(coHopTacXaDaNho);

  // dsSotDaDung gom mọi doc Sọt PO này (đang nhập/đang sửa) từng chạm
  // tới, theo ID — dùng để tính running-total riêng từng sọt và hiện badge
  // "Sọt: <mã>".
  const [dsSotDaDung, setDsSotDaDung] = useState({});
  const [openQuetSot, setOpenQuetSot] = useState(false);
  // Khối/dòng nào đang chờ kết quả quét — { idNhom, idxDong }. idxDong là số
  // = gán thẳng vào ĐÚNG dòng đó (quét lần đầu cho dòng, hoặc đổi sọt của
  // dòng qua badge); idxDong = null = "Thêm sọt" cho khối idNhom, quét
  // xong ĐẨY THÊM 1 dòng mới rỗng vào cuối khối đó.
  const [targetQuet, setTargetQuet] = useState(null);
  // Bấm badge "Sọt: <mã>" của 1 dòng đang mở để đổi thẳng sọt của ĐÚNG dòng
  // đó — menuSotDong lưu (idNhom, idxDong, phần tử DOM neo menu).
  const [menuSotDong, setMenuSotDong] = useState(null);
  // Snapshot items GỐC của PO đang sửa (trước khi user chỉnh sửa trên form) —
  // chỉ có giá trị khi dangSuaPO, dùng để trừ lại phần PO này đã đóng góp vào
  // khoiLuongHienTai của từng Sọt lúc tính running-total (server đã cộng
  // sẵn phần cũ, không trừ ra sẽ bị tính trùng phần đang sửa).
  const [itemsGocDangSua, setItemsGocDangSua] = useState([]);

  // Bản nháp lưu trên Firestore theo (chủ vườn, vườn) — xem api/banNhapThuMua.
  // Chỉ áp dụng khi nhập PO mới, không dùng khi đang sửa 1 PO đã lưu
  // (dangSuaPO đã có nguồn dữ liệu riêng là chính PO đó).
  const [coNhap, setCoNhap] = useState(false);
  const [nhapLuc, setNhapLuc] = useState(null);
  const [dangLuuNhap, setDangLuuNhap] = useState(false);
  const [daLuuNhap, setDaLuuNhap] = useState(false);

  // Realtime danh mục / chủ vườn / vườn / HTX — cập nhật khi người khác sửa.
  useEffect(() => {
    const huyCv = chuVuonApi.langNgheChiTiet(maChuVuon, (chuVuon) => {
      setNgoaiCanh((cur) => ({ ...(cur ?? {}), chuVuon, vuon: cur?.vuon ?? null }));
    });
    const huyVuon = vuonApi.langNgheChiTiet(maVuon, (vuon) => {
      setNgoaiCanh((cur) => ({ ...(cur ?? {}), chuVuon: cur?.chuVuon ?? null, vuon }));
    });
    const huyLb = danhMucApi.langNgheLoaiBo(setLoaiBoList);
    const huyHt = danhMucApi.langNgheHinhThucThanhToan(setHinhThucTtList);
    const huyHtx = hopTacXaApi.langNgheDanhSach(setHopTacXaList);
    return () => {
      huyCv();
      huyVuon();
      huyLb();
      huyHt();
      huyHtx();
    };
  }, [maChuVuon, maVuon]);

  // One-shot nạp form từ PO đang sửa / bản nháp — không listen để tránh
  // ghi đè lúc đang nhập.
  useEffect(() => {
    setDangTai(true);
    const goi = dangSuaPO ? giaoDichApi.chiTiet(suaPOId) : banNhapThuMuaApi.lay(maChuVuon, maVuon);

    goi
      .then(async (poDangSuaHoacNhap) => {
        let nhomLoaiBo;
        let hinhThucTt = "";
        let ghiChu = "";
        let maHopTacXa = layHopTacXaDaNho();
        let goc = [];

        if (dangSuaPO && poDangSuaHoacNhap) {
          const poDangSua = poDangSuaHoacNhap;
          // giaoDichApi.chiTiet đã chạy qua chuanHoaItems (backend) nên mỗi
          // item luôn có sẵn maSot.
          const itemsPO = poDangSua.items.map((it) => ({
            maLoaiBo: it.maLoaiBo,
            khoiLuong: String(it.khoiLuong),
            donGia: String(it.donGia),
            maSot: it.maSot,
          }));
          nhomLoaiBo = nhomHoaItems(itemsPO, true);
          hinhThucTt = poDangSua.hinhThucTt ?? hinhThucTt;
          ghiChu = poDangSua.ghiChu ?? "";
          maHopTacXa = poDangSua.maHopTacXa ?? "";
          goc = itemsPO.map((it) => ({ maSot: it.maSot, khoiLuong: Number(it.khoiLuong) }));
        } else if (!dangSuaPO && poDangSuaHoacNhap) {
          const nhap = poDangSuaHoacNhap;
          nhomLoaiBo = nhomHoaItems(nhap.items ?? [], false);
          hinhThucTt = nhap.hinhThucTt ?? hinhThucTt;
          ghiChu = nhap.ghiChu ?? "";
          maHopTacXa = nhap.maHopTacXa ?? layHopTacXaDaNho();
          setCoNhap(true);
          setNhapLuc(nhap.capNhatLuc);
        } else {
          nhomLoaiBo = [taoKhoiMoi()];
        }

        setForm((cur) => ({
          ...cur,
          nhomLoaiBo,
          hinhThucTt: hinhThucTt || cur.hinhThucTt,
          ghiChu,
          maHopTacXa,
        }));
        setItemsGocDangSua(goc);

        // Nạp mọi Sọt phân biệt mà form đang dùng vào dsSotDaDung —
        // phục vụ running-total và badge "Sọt: <mã>".
        const dsDongPhangInit = nhomLoaiBo.flatMap((n) => n.dong);
        const dsMaSot = [...new Set(dsDongPhangInit.map((d) => d.maSot).filter(Boolean))];
        const sotDocs = await Promise.all(
          dsMaSot.map((idSot) => sotApi.chiTiet(idSot).catch(() => null))
        );
        const map = {};
        sotDocs.forEach((pl) => {
          if (pl) map[pl.id] = pl;
        });
        setDsSotDaDung(map);
      })
      .catch((err) => setLoi(err.message))
      .finally(() => setDangTai(false));
  }, [maChuVuon, maVuon, dangSuaPO, suaPOId]);

  // Khi danh mục hình thức TT realtime về và form chưa chọn — chọn mặc định.
  useEffect(() => {
    if (!hinhThucTtList.length) return;
    setForm((cur) => {
      const hopLe = hinhThucTtList.some((h) => h.id === cur.hinhThucTt);
      if (hopLe) return cur;
      return { ...cur, hinhThucTt: hinhThucTtList[0].id };
    });
  }, [hinhThucTtList]);

  // Vườn lưu địa chỉ theo từng phần (thôn/xã/tỉnh + chi tiết) chứ không có
  // sẵn 1 chuỗi địa chỉ đầy đủ — ghép lại để hiện trên phiếu.
  const diaChiVuon = useMemo(() => {
    const v = ngoaiCanh?.vuon;
    if (!v) return "";
    return [v.diaChiChiTiet, v.thon, v.xa, v.tinh].filter(Boolean).join(", ");
  }, [ngoaiCanh]);

  const dsDongPhang = useMemo(() => form.nhomLoaiBo.flatMap((n) => n.dong), [form.nhomLoaiBo]);

  const thanhTien = useMemo(
    () =>
      dsDongPhang.reduce(
        (tong, d) => tong + (Number(d.khoiLuong) || 0) * (Number(d.donGia) || 0),
        0
      ),
    [dsDongPhang]
  );

  // Khối lượng "sẽ có" trên từng sọt NẾU lưu form ngay lúc này — server
  // khoiLuongHienTai trừ đi phần PO đang sửa này đã đóng góp trước đó (tránh
  // tính trùng khi dangSuaPO, vì server đã cộng sẵn phần cũ), cộng tổng
  // khoiLuong mọi dòng form hiện đang gán đúng sọt đó. Chỉ để hiện banner
  // cảnh báo/UX — số liệu chốt cuối cùng luôn do transaction phía server
  // (giaoDichApi.taoMoi/suaPO) tính lại, không phụ thuộc con số ước lượng này.
  const khoiLuongTheoSot = useMemo(() => {
    const map = {};
    Object.values(dsSotDaDung).forEach((p) => {
      const gocPO = dangSuaPO
        ? itemsGocDangSua.filter((it) => it.maSot === p.id).reduce((s, it) => s + it.khoiLuong, 0)
        : 0;
      map[p.id] = (p.khoiLuongHienTai ?? 0) - gocPO;
    });
    dsDongPhang.forEach((d) => {
      if (!d.maSot) return;
      map[d.maSot] = (map[d.maSot] ?? 0) + (Number(d.khoiLuong) || 0);
    });
    return map;
  }, [dsSotDaDung, dsDongPhang, itemsGocDangSua, dangSuaPO]);

  // Khối lượng CÒN TRỐNG của 1 sọt, KHÔNG TÍNH phần chính dòng đang xem đóng
  // góp (để gõ số vào dòng đó không tự làm giảm chính giới hạn của nó) — hiện
  // dưới ô Khối lượng mỗi dòng để nhân viên biết dòng này còn cân được tối đa
  // bao nhiêu vào đúng sọt nó đang gắn, trước khi sọt đó đầy.
  function conTrongSot(maSotId, khoiLuongDongHienTai) {
    const p = dsSotDaDung[maSotId];
    if (!p || p.khoiLuongToiDa == null) return null;
    const daDungKhongKeDongNay =
      (khoiLuongTheoSot[maSotId] ?? 0) - (Number(khoiLuongDongHienTai) || 0);
    return Math.max(0, p.khoiLuongToiDa - daDungKhongKeDongNay);
  }

  const coKhoiDangMo = form.nhomLoaiBo.some((n) => !n.xong);

  function suaKhoi(idNhom, patch) {
    setForm((f) => ({
      ...f,
      nhomLoaiBo: f.nhomLoaiBo.map((n) => (n.id === idNhom ? { ...n, ...patch } : n)),
    }));
  }

  function suaDongTrongKhoi(idNhom, idxDong, patch) {
    setForm((f) => ({
      ...f,
      nhomLoaiBo: f.nhomLoaiBo.map((n) =>
        n.id !== idNhom
          ? n
          : { ...n, dong: n.dong.map((d, i) => (i === idxDong ? { ...d, ...patch } : d)) }
      ),
    }));
  }

  // Admin đặt giá theo ngày ở trang "Loại bơ & Giá" — chọn loại bơ thì tự
  // điền đơn giá hôm nay cho dòng đầu tiên của khối, đồng thời lưu lại làm
  // TRẦN giá cho cả khối (giaThamChieu, xem khoiDaHoanChinh) — đơn giá PO chỉ
  // được thấp hơn hoặc bằng giá đã đặt, không được cao hơn. Nhân viên vẫn sửa
  // đơn giá thấp xuống được nếu cần (không khoá ô nhập).
  async function chonLoaiBo(idNhom, maLoaiBo) {
    suaKhoi(idNhom, { maLoaiBo });
    try {
      const gia = await giaTheoNgayApi.layGia(maLoaiBo, ngayHomNay());
      suaKhoi(idNhom, { giaThamChieu: gia ?? null });
      if (gia != null) suaDongTrongKhoi(idNhom, 0, { donGia: String(gia) });
    } catch {
      // Không có giá cho hôm nay hoặc lỗi mạng — cứ để nhân viên tự nhập,
      // không giới hạn giá vì chưa biết giá đã đặt là bao nhiêu.
      suaKhoi(idNhom, { giaThamChieu: null });
    }
  }

  function themKhoiMoi() {
    setForm((f) => ({ ...f, nhomLoaiBo: [...f.nhomLoaiBo, taoKhoiMoi()] }));
  }

  // Chỉ cho xoá khi còn >1 khối — luôn phải còn ít nhất 1 khối trên phiếu.
  function xoaKhoi(idNhom) {
    setForm((f) => ({ ...f, nhomLoaiBo: f.nhomLoaiBo.filter((n) => n.id !== idNhom) }));
  }

  // Chỉ cho xoá khi khối đó còn >1 dòng (undo lỡ quét nhầm 1 sọt thừa).
  function xoaDongTrongKhoi(idNhom, idxDong) {
    setForm((f) => ({
      ...f,
      nhomLoaiBo: f.nhomLoaiBo.map((n) =>
        n.id !== idNhom ? n : { ...n, dong: n.dong.filter((_, i) => i !== idxDong) }
      ),
    }));
  }

  function chotKhoi(idNhom) {
    suaKhoi(idNhom, { xong: true });
  }

  // Chỉ cho mở lại 1 khối khi KHÔNG có khối nào khác đang mở — giữ đúng bất
  // biến "tối đa 1 khối mở tại 1 thời điểm". Muốn sửa khối cũ trong lúc khối
  // cuối còn dở, phải xoá hẳn khối cuối trước (nút Xoá khối, chỉ cần >1 khối).
  function moLaiKhoi(idNhom) {
    if (coKhoiDangMo) return;
    suaKhoi(idNhom, { xong: false });
  }

  // Bấm badge "Sọt: <mã>" của 1 dòng — mở menu chọn lại sọt cho ĐÚNG dòng đó.
  function moMenuSot(e, idNhom, idxDong) {
    setMenuSotDong({ el: e.currentTarget, idNhom, idxDong });
  }

  function dongMenuSot() {
    setMenuSotDong(null);
  }

  // Chọn thẳng 1 sọt ĐÃ DÙNG trước đó trong PO này (không cần quét lại) —
  // đổi ngay maSot của đúng dòng đang mở menu, không đụng dòng khác.
  function chonSotChoDong(maSotChon) {
    if (menuSotDong) {
      suaDongTrongKhoi(menuSotDong.idNhom, menuSotDong.idxDong, { maSot: maSotChon });
    }
    dongMenuSot();
  }

  function quetSotMoiChoDong() {
    if (menuSotDong) {
      setTargetQuet({ idNhom: menuSotDong.idNhom, idxDong: menuSotDong.idxDong });
      setOpenQuetSot(true);
    }
    dongMenuSot();
  }

  // Quét QR lần đầu cho dòng chưa có sọt (dong[0] của 1 khối mới).
  function moQuetChoDongTrong(idNhom, idxDong) {
    setTargetQuet({ idNhom, idxDong });
    setOpenQuetSot(true);
  }

  // "Thêm sọt" cho khối đang mở khi sọt của dòng cuối đã đầy — quét xong
  // đẩy thêm 1 dòng mới rỗng vào cuối khối đó (xem xuLyQuetSotMoi).
  function moQuetThemSot(idNhom) {
    setTargetQuet({ idNhom, idxDong: null });
    setOpenQuetSot(true);
  }

  // Sọt mới quét được từ QuetSotDialog — gắn vào đúng khối/dòng đang chờ
  // (targetQuet). idxDong=null nghĩa là "Thêm sọt": đẩy thêm 1 dòng mới,
  // kế thừa đơn giá của dòng liền trước (khối lượng luôn để trống, là số MỚI
  // cho sọt mới). idxDong là số: gán thẳng lại đúng dòng đó (quét lần đầu
  // hoặc đổi sọt qua badge).
  function xuLyQuetSotMoi(sotMoi) {
    setDsSotDaDung((m) => ({ ...m, [sotMoi.id]: sotMoi }));
    setOpenQuetSot(false);
    if (targetQuet) {
      const { idNhom, idxDong } = targetQuet;
      setForm((f) => ({
        ...f,
        nhomLoaiBo: f.nhomLoaiBo.map((n) => {
          if (n.id !== idNhom) return n;
          if (idxDong === null) {
            const dongCuoi = n.dong[n.dong.length - 1];
            return {
              ...n,
              dong: [
                ...n.dong,
                { maSot: sotMoi.id, khoiLuong: "", donGia: dongCuoi?.donGia ?? "" },
              ],
            };
          }
          return {
            ...n,
            dong: n.dong.map((d, i) => (i === idxDong ? { ...d, maSot: sotMoi.id } : d)),
          };
        }),
      }));
    }
    setTargetQuet(null);
  }

  // Sọt cần chặn quét trùng trong QuetSotDialog — đúng sọt HIỆN TẠI của dòng
  // đang chờ gán (idxDong là số), hoặc sọt của dòng CUỐI trong khối (idxDong
  // null, "Thêm sọt" — chính là sọt vừa đầy cần tránh quét lại). Dòng CHƯA
  // có sọt nào (quét lần đầu cho 1 khối mới, dong.maSot còn null) trả về
  // null — KHÔNG fallback về sọt của khối khác, vì trộn nhiều loại bơ chung 1
  // sọt vốn được cho phép (xem thiết kế đã duyệt) — quét đúng sọt khối trước
  // vừa dùng cho khối mới là hành vi hợp lệ, không phải "quét trùng".
  const maSotHienTaiChoQuet = useMemo(() => {
    if (!targetQuet) return null;
    const nhom = form.nhomLoaiBo.find((n) => n.id === targetQuet.idNhom);
    if (!nhom) return null;
    if (targetQuet.idxDong === null) {
      return nhom.dong[nhom.dong.length - 1]?.maSot ?? null;
    }
    return nhom.dong[targetQuet.idxDong]?.maSot ?? null;
  }, [targetQuet, form.nhomLoaiBo]);

  function flattenNhomLoaiBo(nhomLoaiBo) {
    return nhomLoaiBo.flatMap((nhom) =>
      nhom.dong.map((d) => ({
        maLoaiBo: nhom.maLoaiBo,
        khoiLuong: d.khoiLuong,
        donGia: d.donGia,
        maSot: d.maSot,
      }))
    );
  }

  async function luuNhap() {
    setLoi("");
    setDangLuuNhap(true);
    try {
      const nhap = await banNhapThuMuaApi.luu({
        maChuVuon,
        maVuon,
        items: flattenNhomLoaiBo(form.nhomLoaiBo),
        hinhThucTt: form.hinhThucTt,
        ghiChu: form.ghiChu,
        maHopTacXa: form.maHopTacXa,
        nguoiTao: user?.uid,
      });
      setCoNhap(true);
      setNhapLuc(nhap.capNhatLuc);
      setDaLuuNhap(true);
      setTimeout(() => setDaLuuNhap(false), 1500);
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangLuuNhap(false);
    }
  }

  async function boNhap() {
    // Xoá nháp không hoàn tác được — hỏi xác nhận 2 lần để tránh bấm nhầm.
    // eslint-disable-next-line no-alert
    if (!window.confirm("Bỏ bản nháp này? Số liệu đang nhập trên form sẽ không bị xoá.")) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm("Xác nhận lần nữa: xoá hẳn bản nháp đã lưu, không thể khôi phục lại.")) {
      return;
    }
    try {
      await banNhapThuMuaApi.xoa(maChuVuon, maVuon);
      setCoNhap(false);
      setNhapLuc(null);
    } catch (err) {
      setLoi(err.message);
    }
  }

  async function xacNhan(e) {
    e.preventDefault();
    setLoi("");

    if (form.nhomLoaiBo.some((n) => !n.xong)) {
      setLoi('Còn 1 loại bơ chưa bấm "Xong" — hoàn tất hoặc xoá khối đó trước khi lưu.');
      return;
    }

    const vuotSot = Object.entries(khoiLuongTheoSot).some(([id, kg]) => {
      const p = dsSotDaDung[id];
      return p?.khoiLuongToiDa != null && kg > p.khoiLuongToiDa;
    });
    if (vuotSot) {
      setLoi("Có sọt vượt khối lượng tối đa — giảm kg hoặc quét sọt khác.");
      return;
    }

    setDangLuu(true);

    const items = flattenNhomLoaiBo(form.nhomLoaiBo).map((it) => ({
      maLoaiBo: it.maLoaiBo,
      khoiLuong: Number(it.khoiLuong),
      donGia: Number(it.donGia),
      maSot: it.maSot,
    }));

    // Nhớ/quên hợp tác xã cho lần "Nhập thu mua" tiếp theo — làm trước khi
    // gọi API để dù có lỗi mạng vẫn không ảnh hưởng (thao tác thuần cục bộ).
    if (nhoHopTacXa) luuHopTacXaDaNho(form.maHopTacXa);
    else xoaHopTacXaDaNho();

    try {
      if (dangSuaPO) {
        // Sửa PO: ghi đè nguyên mảng items — giaoDichApi.suaPO tự tính lại
        // chênh lệch khối lượng với Sọt trong 1 transaction, không cần
        // biết dòng nào cũ/mới/đã xoá.
        await giaoDichApi.suaPO(suaPOId, {
          items,
          ghiChu: form.ghiChu,
          maHopTacXa: form.maHopTacXa,
          hinhThucTt: form.hinhThucTt,
        });
        navigate(`/thu-mua/xac-nhan/${suaPOId}`);
        return;
      }

      // Mỗi khối loại bơ tự quét sọt của riêng nó, có thể trải nhiều sọt nếu
      // sọt đầu đầy giữa chừng — mỗi item đã tự mang đúng maSot của nó.
      // Gộp toàn bộ loại bơ + mọi sọt trong lượt này vào 1 document duy
      // nhất, trong 1 transaction để hoặc ghi trọn vẹn, hoặc không ghi gì cả
      // nếu có lỗi giữa chừng (vd 1 trong các sọt vừa bị người khác đóng).
      const giaoDich = await giaoDichApi.taoMoi({
        maChuVuon,
        maVuon,
        hinhThucTt: form.hinhThucTt,
        ghiChu: form.ghiChu,
        maHopTacXa: form.maHopTacXa,
        items,
      });
      // Đã lưu thành giao dịch thật — nháp (nếu có) không còn ý nghĩa nữa.
      if (coNhap) banNhapThuMuaApi.xoa(maChuVuon, maVuon).catch(() => {});
      navigate(`/thu-mua/xac-nhan/${giaoDich.id}`);
    } catch (err) {
      setLoi(err.message);
      setDangLuu(false);
    }
  }

  useTour(
    "thu-mua-nhap",
    dangTai || !ngoaiCanh?.chuVuon || !ngoaiCanh?.vuon ? [] : buocFormThuMua()
  );

  if (dangTai || !ngoaiCanh?.chuVuon || !ngoaiCanh?.vuon) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={{ xs: 1.5, md: 3 }}>
          <MDTypography variant="button" color="text">
            Đang tải...
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (loi && !ngoaiCanh?.chuVuon) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={{ xs: 1.5, md: 3 }}>
          <MDAlert color="error">{loi || "Không tải được thông tin giao dịch."}</MDAlert>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={{ xs: 1.5, md: 3 }}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={7} lg={5}>
            <Card sx={{ mb: 3, borderRadius: "16px", overflow: "hidden" }}>
              <MDBox
                sx={{
                  height: "5px",
                  background: `linear-gradient(90deg, ${MAU.skin}, ${MAU.flesh})`,
                }}
              />
              <MDBox p={{ xs: 2, sm: 3 }}>
                <MDTypography
                  sx={{
                    fontFamily: FONT_CHU,
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    letterSpacing: "0.18em",
                    color: MAU.pit,
                  }}
                >
                  {dangSuaPO ? "ĐANG SỬA PHIẾU" : "PHIẾU CÂN TẠI VƯỜN"}
                </MDTypography>
                <MDTypography
                  sx={{
                    fontFamily: FONT_HIEU,
                    fontWeight: 600,
                    fontSize: { xs: "1.35rem", sm: "1.6rem" },
                    color: MAU.ink,
                    mt: 0.25,
                  }}
                >
                  Nhập thu mua
                </MDTypography>

                <MDBox sx={{ borderTop: `1.5px dashed ${alpha(MAU.ink, 0.15)}`, my: 2.5 }} />

                <Grid container rowSpacing={0.5}>
                  <Don nhan="Chủ vườn" giaTri={ngoaiCanh.chuVuon.hoTen} />
                  <Don nhan="Mã chủ vườn" giaTri={ngoaiCanh.chuVuon.maChuVuon || maChuVuon} />
                  <Don nhan="Vườn" giaTri={ngoaiCanh.vuon.tenVuon} />
                  <Don nhan="Địa chỉ" giaTri={diaChiVuon || "—"} />
                </Grid>
              </MDBox>
            </Card>

            <Card sx={{ borderRadius: "16px" }}>
              <MDBox p={{ xs: 2, sm: 3 }} component="form" onSubmit={xacNhan}>
                {!dangSuaPO && coNhap && (
                  <MDBox mb={2.5}>
                    <MDAlert color="info">
                      <MDBox
                        display="flex"
                        flexDirection={{ xs: "column", sm: "row" }}
                        flexWrap="wrap"
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        gap={1}
                        width="100%"
                      >
                        <MDTypography
                          sx={{ fontFamily: FONT_CHU, fontSize: "0.8rem", ...chuXuongDong }}
                        >
                          Đã khôi phục bản nháp lúc{" "}
                          {nhapLuc ? new Date(nhapLuc).toLocaleString("vi-VN") : ""}.
                        </MDTypography>
                        <MDBox
                          component="button"
                          type="button"
                          onClick={boNhap}
                          sx={{
                            all: "unset",
                            cursor: "pointer",
                            fontFamily: FONT_CHU,
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            textDecoration: "underline",
                            flexShrink: 0,
                          }}
                        >
                          Bỏ nháp
                        </MDBox>
                      </MDBox>
                    </MDAlert>
                  </MDBox>
                )}

                <MDBox mb={2.5} id="tour-form-hop-tac-xa">
                  <Nhan>Hợp tác xã (nếu có)</Nhan>
                  <MDInput
                    select
                    SelectProps={{ displayEmpty: true }}
                    value={form.maHopTacXa}
                    onChange={(e) => setForm({ ...form, maHopTacXa: e.target.value })}
                    fullWidth
                    sx={oInput}
                  >
                    <MenuItem value="">Không qua hợp tác xã</MenuItem>
                    {hopTacXaList.map((htx) => (
                      <MenuItem key={htx.id} value={htx.id}>
                        {htx.tenHopTacXa}
                      </MenuItem>
                    ))}
                  </MDInput>
                  <MDBox
                    component="label"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 1,
                      cursor: "pointer",
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={nhoHopTacXa}
                      onChange={(e) => setNhoHopTacXa(e.target.checked)}
                      sx={{ p: 0.5 }}
                    />
                    <MDTypography
                      sx={{
                        fontFamily: FONT_CHU,
                        fontSize: "0.78rem",
                        color: alpha(MAU.ink, 0.65),
                      }}
                    >
                      Nhớ hợp tác xã này cho lần nhập tiếp theo
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <Divider sx={{ mb: 3 }} />

                {form.nhomLoaiBo.map((khoi, idxKhoi) => {
                  const boc = {
                    sx: {
                      pt: idxKhoi > 0 ? 3 : 0,
                      mt: idxKhoi > 0 ? 3 : 0,
                      borderTop: idxKhoi > 0 ? `1.5px dashed ${alpha(MAU.ink, 0.15)}` : "none",
                    },
                  };

                  // Khối đã "Xong" — thu gọn thành dòng tóm tắt, chỉ hiện lại
                  // khi bấm "Sửa".
                  if (khoi.xong) {
                    const tenLoai =
                      loaiBoList.find((lb) => lb.id === khoi.maLoaiBo)?.tenLoai ?? khoi.maLoaiBo;
                    const tongKgKhoi = khoi.dong.reduce(
                      (s, d) => s + (Number(d.khoiLuong) || 0),
                      0
                    );
                    const tongTienKhoi = khoi.dong.reduce(
                      (s, d) => s + (Number(d.khoiLuong) || 0) * (Number(d.donGia) || 0),
                      0
                    );
                    const dsMaSotKhoi = [...new Set(khoi.dong.map((d) => d.maSot).filter(Boolean))];

                    return (
                      <MDBox key={khoi.id} {...boc}>
                        <MDBox
                          sx={{
                            p: 2,
                            borderRadius: "12px",
                            backgroundColor: alpha(MAU.skin, 0.06),
                            border: `1px solid ${alpha(MAU.skin, 0.2)}`,
                          }}
                        >
                          <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            gap={1}
                          >
                            <MDBox display="flex" alignItems="flex-start" gap={1} minWidth={0}>
                              <Icon
                                sx={{ color: MAU.skin, fontSize: "1.25rem !important", mt: "1px" }}
                              >
                                check_circle
                              </Icon>
                              <MDBox minWidth={0}>
                                <Nhan sx={{ mb: 0.25 }}>{`Loại bơ ${idxKhoi + 1}`}</Nhan>
                                <MDTypography
                                  sx={{
                                    fontFamily: FONT_HIEU,
                                    fontWeight: 600,
                                    fontSize: "1.05rem",
                                    color: MAU.ink,
                                    ...chuXuongDong,
                                  }}
                                >
                                  {tenLoai || "—"}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            <MDBox display="flex" alignItems="center" gap={1.5} flexShrink={0}>
                              <MDBox
                                component="button"
                                type="button"
                                onClick={() => moLaiKhoi(khoi.id)}
                                disabled={coKhoiDangMo}
                                sx={{
                                  all: "unset",
                                  cursor: coKhoiDangMo ? "not-allowed" : "pointer",
                                  opacity: coKhoiDangMo ? 0.4 : 1,
                                  fontFamily: FONT_CHU,
                                  fontWeight: 700,
                                  fontSize: "0.72rem",
                                  letterSpacing: "0.03em",
                                  color: MAU.pit,
                                }}
                              >
                                Sửa
                              </MDBox>
                              {form.nhomLoaiBo.length > 1 && (
                                <MDBox
                                  component="button"
                                  type="button"
                                  onClick={() => xoaKhoi(khoi.id)}
                                  sx={{
                                    all: "unset",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    fontFamily: FONT_CHU,
                                    fontWeight: 600,
                                    fontSize: "0.72rem",
                                    color: SEMANTIC.danger,
                                  }}
                                >
                                  <Icon fontSize="small" sx={{ fontSize: "1rem !important" }}>
                                    close
                                  </Icon>
                                </MDBox>
                              )}
                            </MDBox>
                          </MDBox>

                          <MDBox mt={1.25} display="flex" flexWrap="wrap" gap={0.75}>
                            {dsMaSotKhoi.map((idSot) => {
                              // Kg khối này đã cân vào ĐÚNG sọt này (có thể
                              // nhiều dòng cùng gắn 1 sọt) — so với sức chứa
                              // tối đa của sọt, hiện dạng "đã cân/sức chứa".
                              const kgTrongSot = khoi.dong
                                .filter((d) => d.maSot === idSot)
                                .reduce((s, d) => s + (Number(d.khoiLuong) || 0), 0);
                              const toiDa = dsSotDaDung[idSot]?.khoiLuongToiDa;
                              return (
                                <BadgeSot
                                  key={idSot}
                                  maHienThi={dsSotDaDung[idSot]?.maSot}
                                  phu={
                                    toiDa != null
                                      ? `${kgTrongSot.toLocaleString(
                                          "vi-VN"
                                        )}/${toiDa.toLocaleString("vi-VN")} kg`
                                      : `${kgTrongSot.toLocaleString("vi-VN")} kg`
                                  }
                                />
                              );
                            })}
                          </MDBox>

                          <MDBox
                            mt={1.25}
                            pt={1.25}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="baseline"
                            sx={{ borderTop: `1px dashed ${alpha(MAU.ink, 0.15)}` }}
                          >
                            <MDTypography
                              sx={{
                                fontFamily: FONT_CHU,
                                fontSize: "0.8rem",
                                color: alpha(MAU.ink, 0.6),
                              }}
                            >
                              {tongKgKhoi.toLocaleString("vi-VN")} kg
                            </MDTypography>
                            <MDTypography
                              sx={{
                                fontFamily: FONT_HIEU,
                                fontWeight: 600,
                                fontSize: "1rem",
                                color: MAU.skin,
                              }}
                            >
                              {tongTienKhoi.toLocaleString("vi-VN")} đ
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </MDBox>
                    );
                  }

                  // Khối đang mở — nhập dở.
                  const dongCuoi = khoi.dong[khoi.dong.length - 1];
                  const dongCuoiHoanChinh = !!(
                    dongCuoi?.maSot &&
                    dongCuoi?.khoiLuong &&
                    dongCuoi?.donGia
                  );
                  // "Đã đầy" phải tính theo TỔNG đã dùng của sọt (kể cả chính
                  // dòng này) so với sức chứa — dùng khoiLuongTheoSot trực
                  // tiếp, KHÔNG dùng conTrongSot (hàm đó cố tình TRỪ phần
                  // đóng góp của chính dòng đang xem, chỉ đúng cho caption
                  // "còn trống bao nhiêu", không đúng để biết đã đầy hay
                  // chưa — nếu dùng nhầm, nhập ĐÚNG BẰNG hoặc VƯỢT sức chứa
                  // trong 1 lần vẫn không được nhận ra là đã đầy).
                  const sotDongCuoi = dongCuoi?.maSot ? dsSotDaDung[dongCuoi.maSot] : null;
                  const sotDaDay =
                    dongCuoiHoanChinh &&
                    !!sotDongCuoi &&
                    sotDongCuoi.khoiLuongToiDa != null &&
                    (khoiLuongTheoSot[dongCuoi.maSot] ?? 0) >= sotDongCuoi.khoiLuongToiDa;
                  const hoanChinh = khoiDaHoanChinh(khoi);

                  return (
                    <MDBox key={khoi.id} {...boc}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1.5}
                        gap={1}
                      >
                        <Nhan sx={{ mb: 0 }}>{`Loại bơ ${idxKhoi + 1}`}</Nhan>
                        {form.nhomLoaiBo.length > 1 && (
                          <MDBox
                            component="button"
                            type="button"
                            onClick={() => xoaKhoi(khoi.id)}
                            sx={{
                              all: "unset",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.4,
                              fontFamily: FONT_CHU,
                              fontWeight: 600,
                              fontSize: "0.72rem",
                              color: SEMANTIC.danger,
                            }}
                          >
                            <Icon fontSize="small" sx={{ fontSize: "1rem !important" }}>
                              close
                            </Icon>
                            Xoá khối
                          </MDBox>
                        )}
                      </MDBox>

                      {khoi.dong.map((dong, idxDong) => (
                        <MDBox key={idxDong} sx={{ mb: idxDong < khoi.dong.length - 1 ? 2.5 : 0 }}>
                          {/* "Lần cân N" — mỗi dòng trong khối ứng với 1 lượt
                              cân riêng vào 1 sọt cụ thể (dòng 2 trở đi chỉ
                              xuất hiện khi sọt trước đã đầy và bấm "Thêm
                              sọt"). Chỉ hiện số thứ tự khi khối có >1 dòng
                              — giữ gọn giao diện cho trường hợp phổ biến chỉ
                              cân 1 lần/1 sọt. */}
                          {khoi.dong.length > 1 && (
                            <MDTypography
                              sx={{
                                display: "block",
                                mb: 0.5,
                                fontFamily: FONT_CHU,
                                fontWeight: 700,
                                fontSize: "0.62rem",
                                letterSpacing: "0.05em",
                                textTransform: "uppercase",
                                color: alpha(MAU.ink, 0.4),
                              }}
                            >
                              Lần cân {idxDong + 1}
                            </MDTypography>
                          )}
                          {!dong.maSot ? (
                            <NutVienChamChamMau
                              id={idxKhoi === 0 && idxDong === 0 ? "tour-form-quet-sot" : undefined}
                              mau={MAU.pit}
                              icon="qr_code_scanner"
                              onClick={() => moQuetChoDongTrong(khoi.id, idxDong)}
                            >
                              Quét QR Sọt
                            </NutVienChamChamMau>
                          ) : (
                            <>
                              <MDBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={1}
                                gap={1}
                              >
                                <BadgeSot
                                  maHienThi={dsSotDaDung[dong.maSot]?.maSot}
                                  onClick={(e) => moMenuSot(e, khoi.id, idxDong)}
                                />
                                {khoi.dong.length > 1 && (
                                  <MDBox
                                    component="button"
                                    type="button"
                                    onClick={() => xoaDongTrongKhoi(khoi.id, idxDong)}
                                    sx={{
                                      all: "unset",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.3,
                                      fontFamily: FONT_CHU,
                                      fontWeight: 600,
                                      fontSize: "0.68rem",
                                      color: SEMANTIC.danger,
                                    }}
                                  >
                                    <Icon fontSize="small" sx={{ fontSize: "0.9rem !important" }}>
                                      close
                                    </Icon>
                                    Xoá sọt này
                                  </MDBox>
                                )}
                              </MDBox>

                              <Grid container spacing={1.5}>
                                {idxDong === 0 && (
                                  <Grid item xs={12}>
                                    <MDInput
                                      select
                                      SelectProps={{ displayEmpty: true }}
                                      value={khoi.maLoaiBo}
                                      onChange={(e) => chonLoaiBo(khoi.id, e.target.value)}
                                      fullWidth
                                      required
                                      sx={oInput}
                                    >
                                      <MenuItem value="" disabled>
                                        Chọn loại bơ
                                      </MenuItem>
                                      {loaiBoList.map((lb) => (
                                        <MenuItem key={lb.id} value={lb.id}>
                                          {lb.tenLoai}
                                        </MenuItem>
                                      ))}
                                    </MDInput>
                                  </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                  <Nhan>Khối lượng</Nhan>
                                  <MDInputSo
                                    thapPhan
                                    placeholder="0"
                                    value={dong.khoiLuong}
                                    onChange={(v) =>
                                      suaDongTrongKhoi(khoi.id, idxDong, { khoiLuong: v })
                                    }
                                    fullWidth
                                    required
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <DonVi>kg</DonVi>
                                        </InputAdornment>
                                      ),
                                    }}
                                    sx={oInput}
                                  />
                                  {/* Khối lượng tối đa dòng này còn cân được vào
                                      ĐÚNG sọt nó đang gắn — chỉ cảnh báo (đổi
                                      màu đỏ khi vượt), không chặn gõ. Số liệu
                                      chốt cuối cùng luôn do transaction server
                                      tính lại. */}
                                  {(() => {
                                    const conLai = conTrongSot(dong.maSot, dong.khoiLuong);
                                    if (conLai == null) return null;
                                    const vuotQua = (Number(dong.khoiLuong) || 0) > conLai;
                                    // Vượt khối lượng còn trống — cảnh báo RÕ
                                    // (nền đỏ nhạt + icon), không chỉ đổi màu
                                    // chữ, để không lọt qua mắt lúc đang cân
                                    // nhanh ngoài vườn. Không chặn gõ — số liệu
                                    // chốt cuối cùng luôn do transaction server
                                    // tính lại.
                                    if (vuotQua) {
                                      return (
                                        <MDBox
                                          display="flex"
                                          alignItems="center"
                                          gap={0.4}
                                          mt={0.75}
                                          px={1}
                                          py={0.5}
                                          borderRadius="0.5rem"
                                          sx={{ backgroundColor: alpha(SEMANTIC.danger, 0.1) }}
                                        >
                                          <Icon
                                            fontSize="small"
                                            sx={{
                                              fontSize: "0.9rem !important",
                                              color: SEMANTIC.danger,
                                            }}
                                          >
                                            error
                                          </Icon>
                                          <MDTypography
                                            variant="caption"
                                            fontWeight="medium"
                                            sx={{
                                              fontFamily: FONT_CHU,
                                              fontSize: "0.68rem",
                                              color: SEMANTIC.danger,
                                            }}
                                          >
                                            Vượt sọt này — chỉ còn trống{" "}
                                            {conLai.toLocaleString("vi-VN")} kg
                                          </MDTypography>
                                        </MDBox>
                                      );
                                    }
                                    return (
                                      <MDTypography
                                        variant="caption"
                                        sx={{
                                          display: "block",
                                          mt: 0.5,
                                          fontFamily: FONT_CHU,
                                          fontSize: "0.68rem",
                                          color: alpha(MAU.ink, 0.45),
                                        }}
                                      >
                                        Sọt này còn trống tối đa {conLai.toLocaleString("vi-VN")} kg
                                      </MDTypography>
                                    );
                                  })()}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Nhan>Đơn giá</Nhan>
                                  {/* Khoá cứng, không cho nhân viên tự sửa —
                                      đơn giá LUÔN đúng bằng giá đã đặt hôm nay
                                      ở "Loại bơ & Giá" (set lúc chọn loại bơ,
                                      xem chonLoaiBo), không có đường nào gõ
                                      tay ra số khác. */}
                                  <MDInputSo
                                    placeholder="0"
                                    value={dong.donGia}
                                    onChange={() => {}}
                                    disabled
                                    fullWidth
                                    required
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <DonVi>đ/kg</DonVi>
                                        </InputAdornment>
                                      ),
                                    }}
                                    sx={oInput}
                                  />
                                  {!dong.donGia && (
                                    <MDTypography
                                      variant="caption"
                                      sx={{
                                        display: "block",
                                        mt: 0.5,
                                        fontFamily: FONT_CHU,
                                        fontSize: "0.68rem",
                                        color: SEMANTIC.danger,
                                      }}
                                    >
                                      Chưa có giá đặt sẵn hôm nay cho loại bơ này — liên hệ quản lý
                                      đặt giá ở &quot;Loại bơ &amp; Giá&quot; trước khi thu mua.
                                    </MDTypography>
                                  )}
                                </Grid>
                              </Grid>
                            </>
                          )}
                        </MDBox>
                      ))}

                      {dongCuoiHoanChinh && sotDaDay && (
                        <MDBox mt={2.5}>
                          <MDAlert color="warning">
                            <MDTypography sx={{ fontFamily: FONT_CHU, fontSize: "0.8rem" }}>
                              Sọt {dsSotDaDung[dongCuoi.maSot]?.maSot ?? "hiện tại"} đã đầy — bấm
                              &quot;Thêm sọt&quot; để cân tiếp phần dư của loại bơ này vào một sọt
                              khác.
                            </MDTypography>
                          </MDAlert>
                          <MDBox mt={1.5}>
                            <NutVienChamChamMau
                              mau={MAU.pit}
                              icon="qr_code_scanner"
                              onClick={() => moQuetThemSot(khoi.id)}
                            >
                              Thêm sọt
                            </NutVienChamChamMau>
                          </MDBox>
                        </MDBox>
                      )}

                      <MDBox mt={2.5}>
                        <MDButton
                          id={idxKhoi === 0 ? "tour-form-xong" : undefined}
                          type="button"
                          fullWidth
                          disabled={!hoanChinh}
                          onClick={() => chotKhoi(khoi.id)}
                          sx={{
                            py: 1,
                            borderRadius: "10px",
                            boxShadow: "none",
                            fontFamily: FONT_CHU,
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            textTransform: "none",
                            backgroundColor: `${
                              hoanChinh ? MAU.skin : alpha(MAU.ink, 0.12)
                            } !important`,
                            color: `${hoanChinh ? MAU.paper : alpha(MAU.ink, 0.4)} !important`,
                            "&:hover": {
                              backgroundColor: `${MAU.skinDeep} !important`,
                              boxShadow: "none",
                            },
                          }}
                        >
                          <Icon fontSize="small" sx={{ mr: 0.5 }}>
                            check
                          </Icon>
                          Xong
                        </MDButton>
                        {!hoanChinh && (
                          <MDTypography
                            variant="caption"
                            sx={{
                              display: "block",
                              mt: 0.75,
                              textAlign: "center",
                              fontFamily: FONT_CHU,
                              fontSize: "0.68rem",
                              color: alpha(MAU.ink, 0.45),
                            }}
                          >
                            Chọn loại bơ, quét đủ sọt và điền khối lượng/giá để chốt khối này.
                          </MDTypography>
                        )}
                      </MDBox>
                    </MDBox>
                  );
                })}

                {/* Menu chọn lại sọt cho ĐÚNG dòng vừa bấm badge (menuSotDong)
                    — liệt kê mọi sọt đã dùng trong PO này (chọn thẳng, không
                    cần quét lại) + hành động "Quét sọt mới" cho riêng dòng đó. */}
                <Menu anchorEl={menuSotDong?.el ?? null} open={!!menuSotDong} onClose={dongMenuSot}>
                  {Object.values(dsSotDaDung).map((p) => (
                    <MenuItem key={p.id} onClick={() => chonSotChoDong(p.id)}>
                      <Icon fontSize="small" sx={{ mr: 1, color: MAU.pit }}>
                        inventory_2
                      </Icon>
                      Sọt: {p.maSot}
                    </MenuItem>
                  ))}
                  <MenuItem onClick={quetSotMoiChoDong}>
                    <Icon fontSize="small" sx={{ mr: 1, color: MAU.pit }}>
                      qr_code_scanner
                    </Icon>
                    Quét sọt mới cho dòng này
                  </MenuItem>
                </Menu>

                {!coKhoiDangMo && (
                  <MDBox mt={3}>
                    <NutVienChamChamMau mau={MAU.skin} icon="add" onClick={themKhoiMoi}>
                      Thêm loại bơ
                    </NutVienChamChamMau>
                  </MDBox>
                )}

                <MDBox
                  sx={{
                    mt: 3,
                    pt: 2.5,
                    borderTop: `1.5px dashed ${alpha(MAU.ink, 0.18)}`,
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 1,
                  }}
                >
                  <MDTypography
                    sx={{
                      fontFamily: FONT_CHU,
                      fontWeight: 700,
                      fontSize: "0.78rem",
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: alpha(MAU.ink, 0.6),
                    }}
                  >
                    Tổng thành tiền
                  </MDTypography>
                  <MDTypography
                    sx={{
                      fontFamily: FONT_HIEU,
                      fontWeight: 600,
                      fontSize: { xs: "1.45rem", sm: "1.9rem" },
                      color: MAU.skin,
                      ...chuXuongDong,
                    }}
                  >
                    {thanhTien.toLocaleString("vi-VN")} đ
                  </MDTypography>
                </MDBox>

                <Divider sx={{ my: 3 }} />

                <MDBox mb={2.5}>
                  <Nhan>Ghi chú</Nhan>
                  <MDInput
                    placeholder="Ví dụ: bơ giập nhẹ, đã trừ hao bao bì..."
                    value={form.ghiChu}
                    onChange={(e) => setForm({ ...form, ghiChu: e.target.value })}
                    fullWidth
                    multiline
                    rows={2}
                    sx={oGhiChu}
                  />
                </MDBox>
                {loi && (
                  <MDBox mb={2}>
                    <MDAlert color="error">{loi}</MDAlert>
                  </MDBox>
                )}
                {!dangSuaPO && (
                  <MDButton
                    type="button"
                    variant="outlined"
                    fullWidth
                    onClick={luuNhap}
                    disabled={dangLuuNhap || dangLuu}
                    sx={{
                      mb: 1.5,
                      py: 1.25,
                      borderRadius: "10px",
                      fontFamily: FONT_CHU,
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      textTransform: "none",
                      borderColor: alpha(MAU.skin, 0.4),
                      color: `${MAU.skin} !important`,
                      "&:hover": { borderColor: MAU.skin, backgroundColor: alpha(MAU.skin, 0.06) },
                    }}
                  >
                    {dangLuuNhap ? "Đang lưu nháp..." : daLuuNhap ? "Đã lưu nháp" : "Lưu nháp"}
                  </MDButton>
                )}
                <MDButton
                  id="tour-form-luu-giao-dich"
                  type="submit"
                  fullWidth
                  disabled={dangLuu}
                  sx={{
                    py: 1.5,
                    borderRadius: "10px",
                    boxShadow: "none",
                    fontFamily: FONT_HIEU,
                    fontWeight: 600,
                    fontSize: "1rem",
                    textTransform: "none",
                    backgroundColor: `${MAU.skin} !important`,
                    color: `${MAU.paper} !important`,
                    "&:hover": { backgroundColor: `${MAU.skinDeep} !important`, boxShadow: "none" },
                    "&:focus-visible": {
                      outline: `3px solid ${alpha(MAU.pit, 0.6)}`,
                      outlineOffset: "2px",
                    },
                  }}
                >
                  {dangLuu ? "Đang lưu..." : dangSuaPO ? "Lưu thay đổi" : "Lưu giao dịch"}
                </MDButton>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <QuetSotDialog
        open={openQuetSot}
        onDong={() => {
          setOpenQuetSot(false);
          setTargetQuet(null);
        }}
        onQuetDuoc={xuLyQuetSotMoi}
        maSotHienTai={maSotHienTaiChoQuet}
      />
    </DashboardLayout>
  );
}

export default FormThuMua;
